/**
 * What-if state for the Executive Simulation Studio and the Board Report generated from it.
 * The recorded baseline is the business-intelligence expectedLoss block's revenue (the revenue the
 * formula used) and the assessment's industry; anything else the executive typed is a what-if,
 * recomputed by the same formula (services/expectedLoss.ts) and marked as such.
 */
export interface WhatIfContext {
  revenue: number;
  recordedRevenue: number | null;
  industry: string;
  // The industry the formula used; null when not known (never a default).
  recordedIndustry: string | null;
  // Set by the Studio when it already decided (e.g. an untouched, unmeasured revenue field is not a change).
  revenueChanged?: boolean;
}

/** Whole dollars, e.g. $2,735,040. Null or non-finite → "Not measured" (never $0 or $NaN). */
export function formatWholeDollars(amount: number | null | undefined): string {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return 'Not measured';
  const rounded = Math.round(amount);
  return `${rounded < 0 ? '-' : ''}$${Math.abs(rounded).toLocaleString('en-US')}`;
}

export const revenueDiffers = (ctx: WhatIfContext): boolean =>
  ctx.recordedRevenue === null || Math.round(ctx.revenue) !== Math.round(ctx.recordedRevenue);

// Case- and whitespace-insensitive: ' manufacturing ' recorded and 'Manufacturing' selected are the same sector.
const normIndustry = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase();

export const industryDiffers = (ctx: WhatIfContext): boolean =>
  normIndustry(ctx.industry) !== normIndustry(ctx.recordedIndustry);

/** "What-if: revenue $X (recorded $Y); industry A (recorded B)", naming only what changed. */
export function whatIfLine(ctx: WhatIfContext | null | undefined): string | null {
  if (!ctx) return null;
  const parts: string[] = [];
  if (ctx.revenueChanged ?? revenueDiffers(ctx)) {
    const recorded = ctx.recordedRevenue === null ? 'none recorded' : `recorded ${formatWholeDollars(ctx.recordedRevenue)}`;
    // A cleared or 0 revenue is not measured, never "$0".
    const typed = ctx.revenue > 0 ? formatWholeDollars(ctx.revenue) : 'not measured';
    parts.push(`revenue ${typed} (${recorded})`);
  }
  if (industryDiffers(ctx)) {
    const recorded = ctx.recordedIndustry ? `recorded ${ctx.recordedIndustry}` : 'none recorded';
    parts.push(`industry ${ctx.industry || 'not measured'} (${recorded})`);
  }
  return parts.length ? `What-if: ${parts.join('; ')}` : null;
}
